import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import wealthyStewardLogo from "@/assets/wealthy-steward-logo.png";

const OFFICER_ROLES = [
  "President",
  "Vice President",
  "Secretary Treasurer",
  "Secretary",
  "Managing Member",
  "Board Member",
] as const;

const UNAConfirmation = () => {
  const [unaName, setUnaName] = useState("");
  const [constitutionDate, setConstitutionDate] = useState<Date>(new Date());
  const [officerRole, setOfficerRole] = useState<string>("Secretary Treasurer");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("US");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingUna, setCheckingUna] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const checkExistingUNA = async () => {
      if (!user) return;

      try {
        // Reviewer / admin / support accounts have no member UNA and must not
        // be forced through the onboarding wizard.
        const { data: roleRows } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);
        const roles = (roleRows ?? []).map((r: any) => r.role as string);
        if (
          roles.includes("reviewer") ||
          roles.includes("admin") ||
          roles.includes("super_admin") ||
          roles.includes("support")
        ) {
          const dest = roles.includes("reviewer")
            ? "/review"
            : roles.includes("support") && !roles.includes("admin") && !roles.includes("super_admin")
            ? "/support"
            : "/admin/dashboard";
          navigate(dest, { replace: true });
          return;
        }

        // Check if user already has a UNA
        const { data: existingUna } = await supabase
          .from("unas")
          .select("id")
          .eq("treasury_secretary_id", user.id)
          .maybeSingle();

        if (existingUna) {
          // User already has a UNA, redirect to journey
          navigate("/journey", { replace: true });
          return;
        }

        // Check una_metadata for pre-populated data from Ontraport
        const { data: metadata } = await supabase
          .from("una_metadata")
          .select("una_name, constitution_date")
          .eq("user_id", user.id)
          .maybeSingle();

        if (metadata?.una_name) {
          setUnaName(metadata.una_name);
        }
        
        if (metadata?.constitution_date) {
          setConstitutionDate(new Date(metadata.constitution_date));
        }

        // Fetch profile data (canonical source)
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name, address, city, state, zip, country")
          .eq("id", user.id)
          .maybeSingle();

        if (profile?.full_name) {
          setFullName(profile.full_name);
        }

        if (profile?.address) {
          setAddress(profile.address);
          setCity(profile.city || "");
          setState(profile.state || "");
          setZip(profile.zip || "");
          setCountry(profile.country || "US");
        } else {
          // Fallback to OntraPort data if profile address not set
          const { data: fulfillment } = await supabase
            .from("user_fulfillment_progress")
            .select("onboarding_data")
            .eq("user_id", user.id)
            .maybeSingle();

          const onboardingData = fulfillment?.onboarding_data as Record<string, string> | null;
          if (onboardingData) {
            setAddress(onboardingData.legal_address || "");
            setCity(onboardingData.legal_city || "");
            setState(onboardingData.legal_state || "");
            setZip(onboardingData.legal_zip || "");
          }
        }
      } catch (error) {
        console.error("Error checking UNA:", error);
      } finally {
        setCheckingUna(false);
      }
    };

    checkExistingUNA();
  }, [user, navigate]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!unaName.trim()) {
      toast({
        title: "UNA Name Required",
        description: "Please enter a name for your UNA",
        variant: "destructive",
      });
      return;
    }

    if (!constitutionDate) {
      toast({
        title: "Date Constituted Required",
        description: "Please select the date your UNA was constituted",
        variant: "destructive",
      });
      return;
    }

    if (!user) return;

    setLoading(true);

    try {
      // Save name and address to profiles table
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: fullName.trim(),
          address: address.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          country: country.trim(),
        })
        .eq("id", user.id);

      if (profileError) {
        console.error("Error updating profile address:", profileError);
      }

      // Create the UNA with the confirmed name, constitution date, and officer role
      const { error } = await supabase.from("unas").insert({
        name: unaName.trim(),
        treasury_secretary_id: user.id,
        status: "active",
        constitution_date: format(constitutionDate, "yyyy-MM-dd"),
        primary_officer_role: officerRole,
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Welcome to Wealthy Steward!",
        description: "Your UNA has been created successfully.",
      });

      navigate("/journey", { replace: true });
    } catch (error: any) {
      console.error("Error creating UNA:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create your UNA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingUna) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img 
              src={wealthyStewardLogo} 
              alt="Wealthy Steward" 
              className="h-24 w-auto"
            />
          </div>
          <div>
            <CardTitle className="text-2xl">Welcome to Wealthy Steward!</CardTitle>
            <CardDescription className="mt-2">
              Let's confirm the details of your UNA to get started
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleConfirm} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Your Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Enter your full legal name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="h-12 text-lg"
              />
              <p className="text-xs text-muted-foreground">
                Your full legal name as it will appear on UNA documents
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unaName">UNA Name</Label>
              <Input
                id="unaName"
                type="text"
                placeholder="Enter your UNA name (e.g., The Smith Family UNA)"
                value={unaName}
                onChange={(e) => setUnaName(e.target.value)}
                required
                className="h-12 text-lg"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                This is the official name of your Unincorporated Nonprofit Association
              </p>
            </div>

            <div className="space-y-2">
              <Label>Date Constituted</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-12 justify-start text-left font-normal text-lg",
                      !constitutionDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {constitutionDate ? format(constitutionDate, "MMMM d, yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={constitutionDate}
                    onSelect={(date) => date && setConstitutionDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                The date your UNA was officially formed
              </p>
            </div>

            <div className="space-y-2">
              <Label>Your Role in the UNA</Label>
              <Select value={officerRole} onValueChange={setOfficerRole}>
                <SelectTrigger className="h-12 text-lg">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {OFFICER_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Your official title within the UNA
              </p>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div>
                <h3 className="font-medium mb-1">Personal Residence</h3>
                <p className="text-xs text-muted-foreground">
                  This will be your Center for Stewardship Observance
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main Street"
                  className="h-12"
                />
              </div>

              <div className="grid grid-cols-6 gap-2">
                <div className="col-span-3 space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="h-12"
                  />
                </div>
                <div className="col-span-1 space-y-2">
                  <Label htmlFor="state">State / Province</Label>
                  <Input
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="CA"
                    className="h-12"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="zip">ZIP / Postal Code</Label>
                  <Input
                    id="zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder="12345"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="US"
                  className="h-12"
                />
              </div>
            </div>

            <Button
              type="submit" 
              className="w-full h-12 text-base" 
              disabled={loading || !unaName.trim() || !constitutionDate}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Confirm & Continue"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UNAConfirmation;
